<template>
  <div>
    <b-table
        striped
        hover
        responsive
        :items="visibleInvoices"
        :fields="fields"
        class="building-invoice-tbl"
        table-class="invoice-table-fixed"
        show-empty
    >
      <!-- Status badge -->
      <template #cell(invoiceStatus)="{ item }">
        <b-badge :variant="statusVariant(item.invoiceStatus)" class="text-uppercase px-75 py-50">
          {{ item.invoiceStatus }}
        </b-badge>
      </template>

      <!-- Title (ellipsis; full text on hover) -->
      <template #cell(title)="{ item }">
        <span class="invoice-title-text" :title="item.title || '—'">{{ item.title || '—' }}</span>
      </template>

      <!-- Amount with currency -->
      <template #cell(totalAmount)="{ item }">
        <span class="font-weight-bold">{{ formatAmount(item.totalAmount, item.currency) }}</span>
      </template>

      <!-- Creation date -->
      <template #cell(creationDate)="{ item }">
        <span class="text-nowrap text-muted small">{{ formatDate(item.creationDate) }}</span>
      </template>

      <!-- Actions: view-only links -->
      <template #cell(actions)="{ item }">
        <div class="invoice-row-actions">
          <div class="invoice-action-slot">
            <b-button
                v-if="item.links && item.links.pdfLink"
                v-b-tooltip.hover :title="msg('Download PDF')"
                variant="outline-secondary"
                size="sm"
                class="btn-icon invoice-row-btn"
                @click.stop="openLink(item.links.pdfLink)"
            >
              <feather-icon icon="DownloadIcon" size="13"/>
            </b-button>
            <span v-else class="invoice-action-spacer" aria-hidden="true"/>
          </div>
          <div class="invoice-action-slot">
            <b-button
                v-if="item.invoiceStatus && item.invoiceStatus.toUpperCase() === 'OPEN'"
                v-b-tooltip.hover :title="msg('Pay invoice')"
                variant="outline-info"
                size="sm"
                class="btn-icon invoice-row-btn"
                :disabled="payLoadingId === item.invoiceId"
                @click.stop="openPayInvoice(item)"
            >
              <b-spinner v-if="payLoadingId === item.invoiceId" small type="grow"/>
              <feather-icon v-else icon="CreditCardIcon" size="13"/>
            </b-button>
            <span v-else class="invoice-action-spacer" aria-hidden="true"/>
          </div>
        </div>
      </template>

      <template #empty>
        <div class="text-center text-muted py-3">
          <feather-icon icon="FileTextIcon" size="32" class="mb-1"/>
          <p class="mb-0 small">{{ msg('No invoices yet') }}</p>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "BuildingInvoiceTable",
  props: {
    value: {
      type: Object,
    },
  },
  data() {
    return { payLoadingId: null };
  },
  computed: {
    ...mapGetters('translator', ['msg']),

    fields() {
      return [
        { key: 'title', label: this.msg('Title'), thStyle: { width: '140px' }, tdClass: 'invoice-title-td' },
        { key: 'invoiceStatus', label: this.msg('Status'), thStyle: { width: '110px' } },
        { key: 'totalAmount', label: this.msg('Amount'), thStyle: { width: '120px' } },
        { key: 'creationDate', label: this.msg('Created'), thStyle: { width: '120px' } },
        { key: 'actions', label: '', thStyle: { width: '96px', minWidth: '96px' }, tdClass: 'text-right invoice-actions-cell' },
      ];
    },

    // Only show invoices the user needs to act on or has a record of
    visibleInvoices() {
      const all = (this.value && this.value.buildingInvoices) ? this.value.buildingInvoices : [];
      return all.filter(inv => {
        const status = (inv.invoiceStatus || '').toUpperCase();
        return status === 'OPEN' || status === 'PAID';
      });
    },
  },
  methods: {
    ...mapActions('building', ['refreshInvoiceLinks']),

    async openPayInvoice(item) {
      if (item.links && item.links.paymentLink) {
        this.openLink(item.links.paymentLink);
        return;
      }
      this.payLoadingId = item.invoiceId;
      try {
        const building = await this.refreshInvoiceLinks(item.invoiceId);
        const bi = (building.buildingInvoices || []).find((x) => x.invoiceId === item.invoiceId);
        if (bi && bi.links && bi.links.paymentLink) {
          this.openLink(bi.links.paymentLink);
        }
        this.$emit('invoice');
      } catch (e) { /* toast from store */ }
      finally {
        this.payLoadingId = null;
      }
    },

    statusVariant(status) {
      return { OPEN: 'warning', PAID: 'success' }[(status || '').toUpperCase()] || 'secondary';
    },

    formatAmount(amount, currency) {
      if (amount == null) return '—';
      const sym = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
      return `${sym}${parseFloat(amount).toFixed(2)}`;
    },

    formatDate(dateVal) {
      if (!dateVal) return '—';
      try {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch { return '—'; }
    },

    openLink(url) {
      if (url) window.open(url, '_blank');
    },
  },
}
</script>

<style scoped>
.building-invoice-tbl thead th {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}
.btn-icon { line-height: 1; }
.invoice-title-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invoice-row-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  min-width: 84px;
}
.invoice-action-slot {
  flex: 0 0 36px;
  width: 36px;
  min-width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.invoice-action-spacer {
  display: block;
  width: 36px;
  min-width: 36px;
  height: 32px;
}
.invoice-row-btn {
  min-width: 36px;
  width: 36px;
  padding: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.invoice-actions-cell { vertical-align: middle !important; }
</style>

<style>
/* b-table renders table inside its root */
.building-invoice-tbl table.table.invoice-table-fixed {
  table-layout: fixed;
  width: 100%;
}
.building-invoice-tbl td.invoice-title-td {
  overflow: hidden;
  vertical-align: middle !important;
}
</style>
